import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from '@/lib/supabase';

interface SubscriptionPlanFamily {
  id: string;
  name: string;
  description: string | null;
  is_active?: boolean;
  target?: string;
}

interface EditFamilyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  family: SubscriptionPlanFamily | null;
  onSuccess: (updatedFamily: SubscriptionPlanFamily) => void;
}

export default function EditFamilyDialog({ 
  open, 
  onOpenChange, 
  family, 
  onSuccess 
}: EditFamilyDialogProps) {
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editTarget, setEditTarget] = useState('');
  const [loading, setLoading] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const { toast } = useToast();

  // Reset form when family changes
  useEffect(() => {
    if (family && open) {
      setEditName(family.name);
      setEditDescription(family.description || '');
      setEditTarget(family.target || '');
      setIsClosing(false);
    }
  }, [family, open]);

  // Clean up when dialog closes
  useEffect(() => {
    if (!open) {
      const timer = setTimeout(() => {
        setEditName('');
        setEditDescription('');
        setEditTarget('');
        setLoading(false);
        setIsClosing(false);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [open]);

  const resetForm = () => {
    setEditName('');
    setEditDescription('');
    setEditTarget('');
    setLoading(false);
    setIsClosing(false);
  };

  const handleClose = () => {
    if (!loading && !isClosing) {
      setIsClosing(true);
      resetForm();
      onOpenChange(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!family || loading || isClosing) return;

    setLoading(true);

    try {
      const { error } = await supabase
        .from('subscription_plan_families')
        .update({
          name: editName,
          description: editDescription,
          target: editTarget,
          updated_at: new Date().toISOString()
        })
        .eq('id', family.id);

      if (error) throw error;

      const updatedFamily = {
        ...family,
        name: editName,
        description: editDescription,
        target: editTarget
      };
      
      toast({
        title: "Família atualizada",
        description: "A família de planos foi atualizada com sucesso.",
      });

      // Mark as closing before triggering callbacks
      setIsClosing(true);
      
      // Notify parent of success
      onSuccess(updatedFamily);
      
      // Close dialog with small delay to ensure state updates complete
      setTimeout(() => {
        resetForm();
        onOpenChange(false);
      }, 100);
      
    } catch (error) {
      console.error('Error updating family:', error);
      toast({
        variant: "destructive",
        title: "Erro ao atualizar família",
        description: "Não foi possível atualizar a família. Por favor, tente novamente.",
      });
      setLoading(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onOpenChange={(newOpen) => {
        if (!newOpen && !loading && !isClosing) {
          handleClose();
        } else if (newOpen && !isClosing) {
          onOpenChange(newOpen);
        }
      }}
      modal={true}
    >
      <DialogContent 
        onPointerDownOutside={(e) => {
          // Prevent closing while loading or closing
          if (loading || isClosing) {
            e.preventDefault();
          }
        }}
        onEscapeKeyDown={(e) => {
          // Prevent closing while loading or closing
          if (loading || isClosing) {
            e.preventDefault();
          }
        }}
        onInteractOutside={(e) => {
          // Prevent closing while loading or closing
          if (loading || isClosing) {
            e.preventDefault();
          }
        }}>
        <DialogHeader>
          <DialogTitle>Editar Família de Planos</DialogTitle>
          <DialogDescription>
            Faça as alterações necessárias na família de planos.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="edit-name">Nome</Label>
            <Input
              id="edit-name"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              placeholder="Nome da família"
              required
              disabled={loading}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-description">Descrição</Label>
            <Textarea
              id="edit-description"
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              placeholder="Descrição da família"
              disabled={loading}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-target">Target</Label>
            <Input
              id="edit-target"
              value={editTarget}
              onChange={(e) => setEditTarget(e.target.value)}
              placeholder="Ex: Empresas de médio porte"
              disabled={loading}
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={loading || isClosing}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={loading || isClosing}>
              {loading ? 'Salvando...' : 'Salvar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
